import { getCurrentInstance, inject, computed, resolveComponent, openBlock, createElementBlock, normalizeClass, createCommentVNode, createElementVNode, withDirectives, vModelRadio, renderSlot, Fragment, createVNode, createTextVNode, toDisplayString } from 'vue';
import { createComponent } from './utils/create-component';
import propTypes from './utils/prop-types';
import { INPUT_EVENT, CHANGE_EVENT } from './utils/constants';
import { preventDefault } from './utils/dom';
import Icon from './icon';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var props = {
  /**
   * radio类型
   * linear: 默认线性选中类型
   * fill: 填充选中类型
   */
  type: propTypes.string().def('linear'),
  /**
   * 是否选中
   */
  modelValue: propTypes.oneOfType([propTypes.number(), propTypes.string(), propTypes["boolean"]()]),
  /**
   * 配置项
   * {label: xxx, value: xxx, disabled: false}
   */
  option: propTypes.object().def({
    label: '',
    value: '',
    disabled: false
  }),
  /**
   * 是否禁用
   */
  disabled: propTypes["boolean"]().def(false),
  /**
   * 选中icon位置
   */
  align: propTypes.string().def('left'),
  /**
   * label容器justify-content配置
   * start/end/between
   */
  justifyContent: propTypes.string().def('start'),
  /**
   * 是否禁用文本区域点击
   */
  textDisabled: propTypes["boolean"]().def(false)
};
var emits = [INPUT_EVENT, CHANGE_EVENT];

var _createComponent = createComponent('radio'),
  create = _createComponent.create,
  className = _createComponent.className;
var script = create({
  components: _defineProperty({}, Icon.name, Icon),
  props: props,
  /**
   * 支持input change两个事件
   */
  emits: emits,
  setup: function setup(props, _ref) {
    var _instance$parent, _instance$parent$expo;
    var emit = _ref.emit,
      slots = _ref.slots;
    var instance = getCurrentInstance();
    // 中间不存在的情况无需特殊验证
    /* istanbul ignore next */
    var isInGroup = !!(instance !== null && instance !== void 0 && (_instance$parent = instance.parent) !== null && _instance$parent !== void 0 && (_instance$parent$expo = _instance$parent.exposed) !== null && _instance$parent$expo !== void 0 && _instance$parent$expo.isInGroup) || false;
    var uid = instance && instance.uid;
    /**
     * 在radioGroup中，由父组件provide的属性/方法
     * updateRadioGroupValue 更新选中值
     * radioGroupValue radioGroup选中的值
     * isGroupDisabled radioGroup是否被置为禁用
     * groupType radioGroup传入的类型
     * radioGroupName radioGroup的分组名称
     */
    var updateRadioGroupValue;
    var radioGroupValue = false;
    var groupType = null;
    var isGroupDisabled = false;
    var radioGroupName = uid;
    var groupDirection = '';
    if (isInGroup) {
      updateRadioGroupValue = inject('updateRadioGroupValue');
      radioGroupValue = inject('radioGroupValue');
      isGroupDisabled = inject('isGroupDisabled');
      groupType = inject('groupType');
      radioGroupName = inject('radioGroupName');
      groupDirection = inject('groupDirection');
    }
    // radio选中状态
    var checked = computed({
      get: function get() {
        /**
         * radioGroup判断选中list是否包含
         * 单独使用判断传入的value
         */
        if (isInGroup) {
          return radioGroupValue.value === (props.option && props.option.value);
        } else {
          return props.modelValue;
        }
      },
      set: function set(newVal) {
        /**
         * radioGroup中通过方法进行更新
         * 单独使用出发两个事件
         */
        if (isInGroup) {
          updateRadioGroupValue(props.option && props.option.value);
        } else {
          if (newVal) {
            emit(INPUT_EVENT, newVal);
            emit(CHANGE_EVENT, {
              isChecked: newVal,
              option: props.option
            });
          }
        }
      }
    });
    // 显示radio的icon名称
    var iconName = computed(function () {
      var checkedIconName = props.type === 'linear' ? 'base_check' : 'baseflat_select';
      return !checked.value ? 'base_checkbox' : checkedIconName;
    });
    // 整合options和disabled属性后的配置项
    var computedOptions = computed(function () {
      return _objectSpread2(_objectSpread2({}, props.option), {}, {
        disabled: isGroupDisabled || props.disabled || props.option && props.option.disabled || false
      });
    });
    /**
     * radio显示的图标逻辑
     * 1. 是否选中
     * 2. 是否禁用
     */
    var symbolClass = computed(function () {
      var _ref2;
      return _ref2 = {}, _defineProperty(_ref2, "".concat(className, "__symbol"), true), _defineProperty(_ref2, "".concat(className, "__symbol_").concat(groupType || props.type), true), _defineProperty(_ref2, "".concat(className, "__symbol-checked"), checked.value), _defineProperty(_ref2, "".concat(className, "__symbol-disabled"), computedOptions.value.disabled), _ref2;
    });
    /**
     * 自定义图标样式
     */
    var customIconClass = computed(function () {
      var _ref3;
      return _ref3 = {}, _defineProperty(_ref3, "".concat(className, "__custom"), true), _defineProperty(_ref3, "".concat(className, "__custom-disabled"), computedOptions.value.disabled), _ref3;
    });
    /**
     * label容器的class逻辑
     * 1. 容器对齐方式(left/right)
     * 2. 容器flex对齐方式(start/end/space-between)
     */
    var labelContainerClass = computed(function () {
      var _ref4;
      return _ref4 = {}, _defineProperty(_ref4, "".concat(className, "__label"), true), _defineProperty(_ref4, "".concat(className, "__label-").concat(props.align), true), _defineProperty(_ref4, "".concat(className, "__label-").concat(props.justifyContent), true), _ref4;
    });
    /**
     * label文案的class逻辑
     * 1. 对齐方式(left/right)
     */
    var labelTextClass = computed(function () {
      var _ref5;
      return _ref5 = {}, _defineProperty(_ref5, "".concat(className, "__label_text"), true), _defineProperty(_ref5, "".concat(className, "__label_text-").concat(props.align), true), _ref5;
    });
    // 文本禁用点击
    var onChangeText = function onChangeText(e) {
      if (props.textDisabled || props.option && props.option.textDisabled) {
        preventDefault(e, true);
      }
    };
    // 横纵方向展示样式
    var radioClass = computed(function () {
      var _ref6;
      return _ref6 = {}, _defineProperty(_ref6, "".concat(className), true), _defineProperty(_ref6, "".concat(className, "__").concat(groupDirection), true), _ref6;
    });
    return {
      iconName: iconName,
      isInGroup: isInGroup,
      labelContainerClass: labelContainerClass,
      labelTextClass: labelTextClass,
      computedOptions: computedOptions,
      checked: checked,
      className: className,
      symbolClass: symbolClass,
      radioGroupName: radioGroupName,
      onChangeText: onChangeText,
      customIconClass: customIconClass,
      radioClass: radioClass,
      slots: slots
    };
  }
});

var _hoisted_1 = ["name", "disabled", "value"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_sp_icon = resolveComponent("sp-icon");
  return openBlock(), createElementBlock("div", {
    "class": normalizeClass(_ctx.radioClass)
  }, [createCommentVNode(" radio label "), createElementVNode("label", {
    "class": normalizeClass(_ctx.labelContainerClass)
  }, [createCommentVNode(" radio 选中ui部分 "), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__ui_wrapper"))
  }, [withDirectives(createElementVNode("input", {
    ref: "radioInput",
    "class": normalizeClass("".concat(_ctx.className, "__input")),
    name: _ctx.isInGroup ? _ctx.radioGroupName : '',
    type: "radio",
    "onUpdate:modelValue": _cache[0] || (_cache[0] = function ($event) {
      return _ctx.checked = $event;
    }),
    disabled: _ctx.computedOptions.disabled,
    value: _ctx.computedOptions.value
  }, null, 10 /* CLASS, PROPS */, _hoisted_1), [[vModelRadio, _ctx.checked]]), createCommentVNode(" 如果使用自定义图标插槽，显示相插槽内容 "), _ctx.slots.icon ? (openBlock(), createElementBlock("span", {
    key: 0,
    "class": normalizeClass(_ctx.customIconClass)
  }, [renderSlot(_ctx.$slots, "icon", {
    checked: _ctx.checked
  })], 2 /* CLASS */)) : (openBlock(), createElementBlock(Fragment, {
    key: 1
  }, [createCommentVNode(" 未选中/选中态展示相关symbol "), createVNode(_component_sp_icon, {
    "class": normalizeClass(_ctx.symbolClass),
    name: _ctx.iconName
  }, null, 8 /* PROPS */, ["class", "name"])], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))], 2 /* CLASS */), createCommentVNode(" label文案展示区域 "), createElementVNode("div", {
    "class": normalizeClass(_ctx.labelTextClass),
    onClick: _cache[1] || (_cache[1] = function () {
      return _ctx.onChangeText && _ctx.onChangeText.apply(_ctx, arguments);
    })
  }, [_ctx.slots["default"] ? renderSlot(_ctx.$slots, "default", {
    key: 0
  }) : (openBlock(), createElementBlock(Fragment, {
    key: 1
  }, [createTextVNode(toDisplayString(_ctx.computedOptions.label), 1 /* TEXT */)], 2112 /* STABLE_FRAGMENT, DEV_ROOT_FRAGMENT */))], 2 /* CLASS */)], 2 /* CLASS */)], 2 /* CLASS */);
}

script.render = render;
script.__file = "components/src/component/radio/radio.vue";

var _Radio = script;

export { _Radio as default };
